/**
 * Landing Page Detail (Create / Edit)
 * Dashboard form for managing a single landing page
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import {
  getLandingPage,
  createLandingPage,
  updateLandingPage,
  generateSlug,
  isSlugAvailable,
  uploadLandingPageImage,
  uploadLandingPageContent,
} from "../services/landingPage.service";
import { createSegment } from "../services/segments.service";
import RichTextEditor from "../components/content/RichTextEditor";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  PhotoIcon,
  DocumentArrowUpIcon,
} from "@heroicons/react/24/outline";
import type {
  CreateLandingPageInput,
  UpdateLandingPageInput,
} from "../types/landingPage.types";

export default function LandingPageDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isNew = !id;

  // Form state
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [bodyHtml, setBodyHtml] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePosition, setImagePosition] = useState<"left" | "right">("left");
  const [contentUrl, setContentUrl] = useState("");
  const [promoCodeEnabled, setPromoCodeEnabled] = useState(true);
  const [isPublished, setIsPublished] = useState(false);
  const [segmentId, setSegmentId] = useState<string | null>(null);

  // UI state
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Load existing landing page
  const { data: landingPage, isLoading } = useQuery({
    queryKey: ["landing-page", id],
    queryFn: () => getLandingPage(id!),
    enabled: !!id,
  });

  // Populate form when data loads
  useEffect(() => {
    if (landingPage) {
      setTitle(landingPage.title);
      setSlug(landingPage.slug);
      setSlugManuallyEdited(true);
      setBodyHtml(landingPage.body_html || "");
      setImageUrl(landingPage.image_url || "");
      setImagePosition(landingPage.image_position);
      setContentUrl(landingPage.content_url || "");
      setPromoCodeEnabled(landingPage.promo_code_enabled);
      setIsPublished(landingPage.is_published);
      setSegmentId(landingPage.segment_id);
    }
  }, [landingPage]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!slugManuallyEdited && title) {
      setSlug(generateSlug(title));
    }
  }, [title, slugManuallyEdited]);

  // Validate slug on change
  useEffect(() => {
    if (!slug) {
      setSlugError(null);
      return;
    }
    const timeout = setTimeout(async () => {
      const available = await isSlugAvailable(slug, id);
      setSlugError(available ? null : "This slug is already taken");
    }, 500);
    return () => clearTimeout(timeout);
  }, [slug, id]);

  const handleSlugChange = (val: string) => {
    setSlugManuallyEdited(true);
    setSlug(
      val
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "-")
        .replace(/-+/g, "-"),
    );
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const url = await uploadLandingPageImage(file);
      setImageUrl(url);
    } catch (err) {
      console.error("Upload failed:", err);
      setError("Image upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleContentUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const url = await uploadLandingPageContent(file);
      setContentUrl(url);
    } catch (err) {
      console.error("Content upload failed:", err);
      setError("Content upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!slug.trim()) {
      setError("Slug is required");
      return;
    }
    if (slugError) {
      setError("Please fix the slug before saving");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      if (isNew) {
        // Auto-create segment on first save
        let newSegmentId = segmentId;
        if (!newSegmentId) {
          const seg = await createSegment(
            {
              name: `LP: ${title.trim()}`,
              description: `Auto-created segment for landing page: ${title.trim()}`,
              is_dynamic: false,
              query_criteria: { groups: [], logic: "AND" },
            },
            user?.id || "",
          );
          newSegmentId = seg.id;
          setSegmentId(newSegmentId);
        }

        const input: CreateLandingPageInput = {
          title: title.trim(),
          slug: slug.trim(),
          body_html: bodyHtml,
          image_url: imageUrl || undefined,
          image_position: imagePosition,
          content_url: contentUrl || undefined,
          promo_code_enabled: promoCodeEnabled,
          is_published: isPublished,
          segment_id: newSegmentId || undefined,
        };

        const created = await createLandingPage(input, user?.id);
        queryClient.invalidateQueries({ queryKey: ["landing-pages"] });
        setSuccessMsg("Landing page created!");
        // Navigate to edit mode
        navigate(`/landing-pages/${created.id}`, { replace: true });
      } else {
        const updates: UpdateLandingPageInput = {
          title: title.trim(),
          slug: slug.trim(),
          body_html: bodyHtml,
          image_url: imageUrl || undefined,
          image_position: imagePosition,
          content_url: contentUrl || undefined,
          promo_code_enabled: promoCodeEnabled,
          is_published: isPublished,
        };

        // Auto-create segment if missing
        if (!segmentId) {
          const seg = await createSegment(
            {
              name: `LP: ${title.trim()}`,
              description: `Auto-created segment for landing page: ${title.trim()}`,
              is_dynamic: false,
              query_criteria: { groups: [], logic: "AND" },
            },
            user?.id || "",
          );
          updates.segment_id = seg.id;
          setSegmentId(seg.id);
        }

        await updateLandingPage(id!, updates, user?.id);
        queryClient.invalidateQueries({ queryKey: ["landing-pages"] });
        queryClient.invalidateQueries({ queryKey: ["landing-page", id] });
        setSuccessMsg("Saved!");
      }

      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      console.error("Save failed:", err);
      setError(err?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUrl = () => {
    const url = `${window.location.origin}/lp/${slug}`;
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handlePreview = () => {
    window.open(`/lp/${slug}?preview=true`, "_blank");
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading landing page..." />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      {/* Back button + title */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/landing-pages")}
          className="p-1.5 text-gray-500 hover:text-gray-700 rounded"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNew ? "Create Landing Page" : "Edit Landing Page"}
        </h1>
      </div>

      {/* Error / Success */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          {successMsg}
        </div>
      )}

      <div className="space-y-6">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Firearm Safety Rules"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Slug */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Slug <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">/lp/</span>
            <input
              type="text"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="firearm-safety-rules"
              className={`flex-1 px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                slugError ? "border-red-300" : "border-gray-300"
              }`}
            />
          </div>
          {slugError && (
            <p className="text-xs text-red-500 mt-1">{slugError}</p>
          )}
        </div>

        {/* Image upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image
          </label>
          {imageUrl ? (
            <div className="relative inline-block">
              <img
                src={imageUrl}
                alt="Landing page"
                className="max-h-48 rounded-lg border border-gray-200"
              />
              <button
                onClick={() => setImageUrl("")}
                className="absolute top-2 right-2 bg-white/80 rounded-full p-1 text-gray-600 hover:text-red-600"
                title="Remove image"
              >
                &times;
              </button>
            </div>
          ) : (
            <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors">
              <PhotoIcon className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500">
                {uploading ? "Uploading..." : "Click to upload an image"}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          )}
        </div>

        {/* Image position */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image Position
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setImagePosition("left")}
              className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                imagePosition === "left"
                  ? "bg-blue-50 border-blue-300 text-blue-700"
                  : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              Left
            </button>
            <button
              type="button"
              onClick={() => setImagePosition("right")}
              className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                imagePosition === "right"
                  ? "bg-blue-50 border-blue-300 text-blue-700"
                  : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              Right
            </button>
          </div>
        </div>

        {/* Body content */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Body Content
          </label>
          {/* Only render editor when data is ready (edit mode) or immediately (create mode).
              Pass landingPage content directly to avoid useEffect timing issue. */}
          {(isNew || landingPage) && (
            <RichTextEditor
              key={landingPage?.id || "new"}
              content={landingPage?.body_html || ""}
              onChange={setBodyHtml}
              placeholder="Write your landing page content..."
            />
          )}
        </div>

        {/* Downloadable content */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Downloadable Content
          </label>
          <p className="text-xs text-gray-500 mb-2">
            File sent to the user after form submission (e.g. PDF guide, safety
            rules)
          </p>
          {contentUrl ? (
            <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <DocumentArrowUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <a
                href={contentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline truncate flex-1"
              >
                {contentUrl.split("/").pop()}
              </a>
              <button
                type="button"
                onClick={() => setContentUrl("")}
                className="text-sm text-red-500 hover:text-red-700"
              >
                Remove
              </button>
            </div>
          ) : (
            <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors">
              <DocumentArrowUpIcon className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500">
                {uploading
                  ? "Uploading..."
                  : "Click to upload content file (PDF, document, etc.)"}
              </span>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.png,.jpg,.jpeg"
                onChange={handleContentUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          )}
        </div>

        {/* Promo code toggle */}
        <div className="flex items-center gap-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={promoCodeEnabled}
              onChange={(e) => setPromoCodeEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
          <span className="text-sm text-gray-700">
            Show promo code field on form
          </span>
        </div>

        {/* Published toggle */}
        <div className="flex items-center gap-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
          </label>
          <span className="text-sm text-gray-700">Published</span>
        </div>

        {/* Segment info */}
        {segmentId && (
          <div className="text-sm text-gray-500">
            Linked segment: <span className="font-mono">{segmentId}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
          >
            {saving ? "Saving..." : isNew ? "Create" : "Save"}
          </button>

          {!isNew && slug && (
            <>
              <button
                onClick={handlePreview}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                Preview
              </button>
              <button
                onClick={handleCopyUrl}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <ClipboardDocumentIcon className="w-4 h-4" />
                {copiedUrl ? "Copied!" : "Copy URL"}
              </button>
            </>
          )}

          <button
            onClick={() => navigate("/landing-pages")}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
